<script lang="ts">
  import { onMount } from "svelte";
  import { resizableColumns } from "$lib/columnWidths.svelte";
  import { _ } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import { api } from "$lib/api";

  let { params }: { params: { slug: string } } = $props();
  const slug = $derived(params?.slug ?? "");

  type LinkInfo = {
    id: number;
    buyer_tenant: { slug: string; display_name: string; country: string } | null;
    seller_tenant: { slug: string; display_name: string; country: string } | null;
  };
  type PreviewRow = {
    seq: number;
    image: string | null;
    code: string;
    name: string;
    units_per_pkg: number | null;
    price: number | null;
    currency: string;
  };

  let links = $state<LinkInfo[]>([]);
  let selectedCountry = $state("");
  let selectedLinkId = $state<number | null>(null);
  let format = $state<"xlsx" | "pdf">("xlsx");
  let preview = $state<PreviewRow[]>([]);
  let loadingLinks = $state(true);
  let loadingPreview = $state(false);
  let generating = $state(false);
  let error = $state("");

  let countries = $derived(
    [...new Set(links.map(l => l.buyer_tenant?.country || l.seller_tenant?.country || "").filter(Boolean))].sort()
  );
  let filteredLinks = $derived(
    selectedCountry ? links.filter(l => (l.buyer_tenant?.country || l.seller_tenant?.country) === selectedCountry) : links
  );

  function partnerName(link: LinkInfo): string {
    return link.buyer_tenant?.display_name || `Link #${link.id}`;
  }

  async function loadLinks() {
    loadingLinks = true;
    try {
      const allLinks = await api.get<LinkInfo[]>(`/t/${slug}/supplier-links`);
      // Price lists go out to *our* buyers — drop links where we buy.
      links = allLinks.filter((l) => l.seller_tenant?.slug === slug);
    } catch (e) { console.error(e); }
    finally { loadingLinks = false; }
  }

  async function loadPreview() {
    if (!selectedLinkId) { preview = []; return; }
    loadingPreview = true;
    try {
      preview = await api.get<PreviewRow[]>(`/t/${slug}/pricing/price-list/preview?link_id=${selectedLinkId}`);
    } catch (e) {
      preview = [];
      console.error(e);
    } finally { loadingPreview = false; }
  }

  $effect(() => { if (selectedLinkId) loadPreview(); });

  async function generate() {
    if (!selectedLinkId) return;
    generating = true;
    error = "";
    try {
      const response = await fetch(`/api/v1/t/${slug}/pricing/price-list/export?link_id=${selectedLinkId}&format=${format}`, {
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `price-list-${slug}-${selectedLinkId}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      error = $_("common.actionFailed");
    } finally { generating = false; }
  }

  onMount(() => { if (slug) loadLinks(); });
</script>

<AppShell tenantSlug={slug} active="pricing/price-list">
  <div class="pl-page">
    <div class="pl-header">
      <h1 class="pl-title">{$_("nav.pricingPriceList")}</h1>
      <p class="pl-desc">Generate a branded PDF or Excel price list for a specific distributor.</p>
    </div>

    <div class="pl-form">
      {#if countries.length > 1}
        <div class="form-group">
          <label for="plg_country">Country</label>
          <select id="plg_country" bind:value={selectedCountry} onchange={() => { selectedLinkId = null; preview = []; }}>
            <option value="">All countries</option>
            {#each countries as c}<option value={c}>{c}</option>{/each}
          </select>
        </div>
      {/if}

      <div class="form-group">
        <label for="plg_distributor">Distributor</label>
        <select id="plg_distributor" bind:value={selectedLinkId}>
          <option value={null}> - select distributor - </option>
          {#each filteredLinks as link}
            <option value={link.id}>{partnerName(link)}</option>
          {/each}
        </select>
      </div>

      <div class="form-group">
        <span class="form-label-text" id="plg_format_label">Format</span>
        <div class="format-row" role="radiogroup" aria-labelledby="plg_format_label">
          <label class="format-opt" class:selected={format === "xlsx"}>
            <input type="radio" name="format" value="xlsx" bind:group={format} />
            <span class="material-icons-outlined">table_chart</span> Excel (.xlsx)
          </label>
          <label class="format-opt" class:selected={format === "pdf"}>
            <input type="radio" name="format" value="pdf" bind:group={format} />
            <span class="material-icons-outlined">picture_as_pdf</span> PDF
          </label>
        </div>
      </div>

      {#if error}<p class="error-msg">{error}</p>{/if}

      <button class="btn btn--primary" onclick={generate} disabled={!selectedLinkId || generating}>
        {#if generating}
          <span class="material-icons-outlined spin">sync</span> Generating…
        {:else}
          <span class="material-icons-outlined">download</span> Generate &amp; Download
        {/if}
      </button>
    </div>

    {#if selectedLinkId}
      <div class="preview-section">
        <h2 class="preview-title">Preview</h2>
        {#if loadingPreview}
          <div class="loading">{$_("common.loading")}</div>
        {:else if preview.length === 0}
          <p class="preview-empty">No products with prices for this distributor yet.</p>
        {:else}
          <div class="preview-wrap">
            <table use:resizableColumns={"price-list-preview"} class="preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Photo</th>
                  <th>Code</th>
                  <th>Product</th>
                  <th class="num-cell">Units/Pkg</th>
                  <th class="price-cell">Price</th>
                </tr>
              </thead>
              <tbody>
                {#each preview as row}
                  <tr>
                    <td>{row.seq}</td>
                    <td class="img-cell">
                      {#if row.image}
                        <img src={row.image} alt={row.name} class="prod-thumb" />
                      {:else}
                        <div class="prod-thumb-placeholder">
                          <span class="material-icons-outlined">image</span>
                        </div>
                      {/if}
                    </td>
                    <td class="code-cell">{row.code}</td>
                    <td>{row.name}</td>
                    <td class="num-cell">{row.units_per_pkg ?? "-"}</td>
                    <td class="price-cell">{row.price != null ? `${row.price.toFixed(2)} ${row.currency}` : "-"}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</AppShell>

<style>
  .pl-page { display: flex; flex-direction: column; gap: 24px; }
  .pl-header h1 { margin: 0; font-size: 22px; font-weight: 700; }
  .pl-desc { margin: 4px 0 0; font-size: 14px; color: var(--color-text-muted); }
  .pl-form { background: #fff; border: 1px solid var(--color-divider); border-radius: 8px; padding: 24px; display: flex; flex-direction: column; gap: 18px; max-width: 480px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group label, .form-group .form-label-text { font-size: 13px; font-weight: 600; color: var(--color-text-muted); }
  .form-group select { padding: 8px 12px; border: 1px solid var(--color-divider); border-radius: 6px; font-size: 14px; font-family: inherit; }
  .format-row { display: flex; gap: 10px; }
  .format-opt { display: flex; align-items: center; gap: 6px; padding: 9px 14px; border: 1.5px solid var(--color-divider); border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--color-text-muted); user-select: none; }
  .format-opt.selected { border-color: var(--color-primary); color: var(--color-primary); background: rgba(33,133,213,0.04); }
  .format-opt input { display: none; }
  .format-opt .material-icons-outlined { font-size: 18px !important; }
  .error-msg { color: #c62828; font-size: 13px; margin: 0; }
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 20px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; font-family: inherit; }
  .btn--primary { background: var(--color-primary); color: #fff; }
  .btn--primary:disabled { opacity: 0.55; cursor: default; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .spin { animation: spin 1s linear infinite; }
  .preview-section { display: flex; flex-direction: column; gap: 12px; }
  .preview-title { margin: 0; font-size: 16px; font-weight: 700; color: var(--color-surface-darker); }
  .preview-empty { color: var(--color-text-muted); font-size: 14px; }
  .loading { padding: 32px; text-align: center; color: var(--color-text-muted); }
  .preview-wrap { background: #fff; border: 1px solid var(--color-divider); border-radius: 8px; overflow: auto; }
  .preview-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .preview-table th { padding: 10px 12px; border-bottom: 1px solid var(--color-divider); background: #fafafa; font-size: 11px; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase; text-align: left; }
  .preview-table td { padding: 8px 12px; border-bottom: 1px solid var(--color-divider); }
  .preview-table tr:last-child td { border-bottom: none; }
  .img-cell { width: 52px; }
  .prod-thumb { width: 40px; height: 40px; object-fit: cover; border-radius: 4px; border: 1px solid #eee; }
  .prod-thumb-placeholder { width: 40px; height: 40px; background: #f5f5f5; border-radius: 4px; border: 1px solid #eee; display: flex; align-items: center; justify-content: center; color: #ddd; }
  .prod-thumb-placeholder .material-icons-outlined { font-size: 20px !important; }
  .code-cell { color: var(--color-text-muted); font-size: 12px; }
  .num-cell, .price-cell, .preview-table th.num-cell, .preview-table th.price-cell { text-align: right; }
  .price-cell { font-weight: 600; color: var(--color-primary); }
</style>
